import { getEidFromIdTokenInCookie } from "../authentication";
import { FAC_CONFIG_TYPES, FAC_EVENT_TYPE, FAC_FUNCTIONALITY } from "../constants";

export const featureAccessInitialErrorState = {
  key: "",
  label: "",
  path: "",
  adminPath: "",
};

export const blankFeatureConfigItem = {
  accessRequirements: [],
  adminPath: "",
  childItems: [],
  description: "",
  isActive: false,
  isActiveOnMobile: false,
  isHamburgerOption: true,
  isHomeButton: false,
  key: "",
  label: "",
  overriddenEids: [],
  path: "",
  reports: [],
  stations: [],
};

export const blankChildConfigItem = {
  accessRequirements: [],
  description: "",
  isActive: false,
  isActiveOnMobile: false,
  isHamburgerOption: false,
  isHomeButton: false,
  key: "",
  label: "",
  overriddenEids: [],
  parentFeatureKey: "",
  path: "",
  stations: "",
};

export const allAccessRequirements = ["admin", "superuser", "IAM", "TWU", "Non-Contract"];

export const buildFACPayload = (currentConfigItem, metadata, stations = null) => {
  let payload = {};
  payload["eventType"] = metadata.eventType;
  payload["faConfigType"] = metadata.configType;
  payload["createdBy"] = getEidFromIdTokenInCookie();
  payload["configItem"] = buildConfigItem(currentConfigItem, metadata, stations);
  return payload;
};

const buildConfigItem = (currentConfigItem, metadata, stations) => {
  let configItem = {};
  if (metadata.configType === FAC_CONFIG_TYPES.FEATURE || metadata.configType === FAC_CONFIG_TYPES.CHILD_ITEM) {
    configItem["key"] = currentConfigItem["key"];
    configItem["label"] = currentConfigItem["label"];
    configItem["path"] = currentConfigItem["path"];
    configItem["description"] = currentConfigItem["description"];
    configItem["isHamburgerOption"] = currentConfigItem["isHamburgerOption"];
    configItem["isHomeButton"] = currentConfigItem["isHomeButton"];
    configItem["isActiveOnMobile"] = currentConfigItem["isActiveOnMobile"];
    configItem["isActive"] = currentConfigItem["isActive"];
    configItem["stations"] = stations;
    configItem["overriddenEids"] = currentConfigItem["overriddenEids"];
    configItem["accessRequirements"] = currentConfigItem["accessRequirements"];

    if (metadata.configType === FAC_CONFIG_TYPES.FEATURE) {
      configItem["adminPath"] = currentConfigItem["adminPath"];
      configItem["reports"] = currentConfigItem["reports"];
    }

    if (metadata.configType === FAC_CONFIG_TYPES.CHILD_ITEM) {
      configItem["parentFeatureKey"] = currentConfigItem["parentFeatureKey"];
    }
  }

  if (metadata.configType === FAC_CONFIG_TYPES.ACCESS_LINKS) {
    let links = {};
    for (const feature in currentConfigItem) {
      links[feature] = currentConfigItem[feature];
    }

    configItem["accessLevel"] = metadata.accessLevel;
    configItem["links"] = links;
  }

  return configItem;
};

export const getUploadMetadata = (action, isChildItem, selectedAccessLevel) => {
  let metaData = {};
  switch (action) {
    case FAC_FUNCTIONALITY.CREATE_FTR:
      metaData["configType"] = FAC_CONFIG_TYPES.FEATURE;
      metaData["eventType"] = FAC_EVENT_TYPE.CREATE;
      break;
    case FAC_FUNCTIONALITY.CREATE_CI:
      metaData["configType"] = FAC_CONFIG_TYPES.CHILD_ITEM;
      metaData["eventType"] = FAC_EVENT_TYPE.CREATE;
      break;
    case FAC_FUNCTIONALITY.EDIT_FTR_OR_CI:
      if (isChildItem) {
        metaData["configType"] = FAC_CONFIG_TYPES.CHILD_ITEM;
      } else {
        metaData["configType"] = FAC_CONFIG_TYPES.FEATURE;
      }
      metaData["eventType"] = FAC_EVENT_TYPE.UPDATE;
      break;
    case FAC_FUNCTIONALITY.EDIT_ACS_LNKS:
      metaData["configType"] = FAC_CONFIG_TYPES.ACCESS_LINKS;
      metaData["eventType"] = FAC_EVENT_TYPE.UPDATE;
      metaData["accessLevel"] = selectedAccessLevel;
      break;
    default:
      break;
  }
  return metaData;
};

export const getS3FileInfix = (payload) => {
  switch (payload["faConfigType"]) {
    case FAC_CONFIG_TYPES.FEATURE:
      return `FTR_${payload["configItem"]["key"]}`;
    case FAC_CONFIG_TYPES.CHILD_ITEM:
      return `CI_${payload["configItem"]["key"]}`;
    case FAC_CONFIG_TYPES.ACCESS_LINKS:
      return `ACSLNKS_${payload["configItem"]["accessLevel"]}`;
    default:
      console.error("Unknown config type for file name.");
      break;
  }
};

export const findFeatureByKey = (userFeatures, key) => {
  const allChildren = userFeatures.flatMap((item) => item.childItems || []);

  const foundInChildren = allChildren.find((child) => child.key === key);

  if (foundInChildren) {
    return foundInChildren;
  }

  const foundInParents = userFeatures.find((item) => item.key === key);

  return foundInParents;
};

export const isFeatureActive = (userFeatures, key) => {
  return !!findFeatureByKey(userFeatures, key);
};
export const isFeatureHomeButtonActive = (userFeatures, key) => {
  const feature = findFeatureByKey(userFeatures, key);
  return feature?.isHomeButton;
};

export const isAdminFeature = (feature) => {
  return feature.accessRequirements.includes("admin") || feature.accessRequirements.includes("superuser");
};

export const getAdminQuickLinksFromFAC = (userFeatures, quickLinks, isDesktopView) => {
  const newQuickLinks = [...quickLinks];
  const quickLinksFAC = userFeatures.sort(function (a, b) {
    return (
      newQuickLinks.findIndex((item) => item.key === a.key) - newQuickLinks.findIndex((item) => item.key === b.key)
    );
  });
  for (const feature of quickLinksFAC) {
    if (
      isAdminFeature(feature) &&
      feature.isHomeButton &&
      (isDesktopView || feature.isActiveOnMobile) &&
      !newQuickLinks.some((link) => link.key === feature.key)
    ) {
      newQuickLinks.push({
        key: feature.key,
        feature,
      });
    }

    for (const childItem of feature.childItems) {
      if (
        isAdminFeature(childItem) &&
        childItem.isHomeButton &&
        (isDesktopView || childItem.isActiveOnMobile) &&
        !newQuickLinks.some((link) => link.key === childItem.key)
      ) {
        newQuickLinks.push({
          key: childItem.key,
          feature: childItem,
        });
      }
    }
  }
  return newQuickLinks;
};

export const addEmployeeQuickLinksFromFAC = (userFeatures, quickLinks, isDesktopView) => {
  const newQuickLinks = [...quickLinks];
  const quickLinksFAC = userFeatures.sort(function (a, b) {
    return (
      newQuickLinks.findIndex((item) => item.key === a.key) - newQuickLinks.findIndex((item) => item.key === b.key)
    );
  });
  for (const feature of quickLinksFAC) {
    if (
      !isAdminFeature(feature) &&
      feature.isHomeButton &&
      (isDesktopView || feature.isActiveOnMobile) &&
      !newQuickLinks.some((link) => link.key === feature.key)
    ) {
      newQuickLinks.push({
        key: feature.key,
        feature,
      });
    }

    for (const childItem of feature.childItems) {
      if (
        !isAdminFeature(childItem) &&
        childItem.isHomeButton &&
        (isDesktopView || childItem.isActiveOnMobile) &&
        !newQuickLinks.some((link) => link.key === childItem.key)
      ) {
        newQuickLinks.push({
          key: childItem.key,
          feature: childItem,
        });
      }
    }
  }
  return newQuickLinks;
};
